import type { Rect, UiRects } from "../../game/input";
import { CANVAS_WIDTH } from "../../game/constants";
import { isMobileViewport, mobileFontSize } from "../../game/platform";
import { getCachedOwnedIds, getCachedEquipped } from "../../economy/inventoryManager";
import type { RenderModel } from "../renderModel";
import type { drawButton } from "../ui";
import {
  GAME_FONT,
  MENU_CONTENT_OFFSET_PX,
  MENU_TEXT_PRIMARY,
  uiCloseSize,
  UI_CLOSE_Y,
  UI_EDGE_PAD,
  formatHms,
  menuBtnH,
  menuButtonColumns,
  menuGap,
  mFont,
  mFontPlain,
} from "../renderConstants";

// ── Mutable slide-animation state ────────────────────────────────────

export interface ShopSlideState {
  shopPrevIdx: number;
  shopPrevSkinPath: string;
  shopSlideStartMs: number;
  shopSlideDir: number;
}

// ── Dependency interface ────────────────────────────────────────────

export interface ShopDeps {
  ctx: CanvasRenderingContext2D;
  canvasHeight: number;
  nowMs: () => number;
  gemShopDoorImage: HTMLImageElement | null;
  slideState: ShopSlideState;
  drawBtn: (rect: Rect, label: string, options?: Parameters<typeof drawButton>[3]) => void;
  drawCircleCloseButton: (rect: Rect) => void;
  drawGemIcon: (x: number, y: number, size: number) => void;
  drawCharacterPortrait: (path: string, x: number, y: number, w: number, h: number, verticalAlign: "center" | "bottom") => void;
  drawCircleArrowButton: (rect: Rect, dir: -1 | 1) => void;
  measureTextWidth: (text: string) => number;
}

// ── Extracted screen function ───────────────────────────────────────

export function drawShopScreen(deps: ShopDeps, model: RenderModel): UiRects {
  const {
    ctx,
    canvasHeight: canvasH,
    nowMs,
    gemShopDoorImage: doorImg,
    slideState,
    drawBtn,
    drawCircleCloseButton,
    drawGemIcon,
    drawCharacterPortrait,
    drawCircleArrowButton,
    measureTextWidth,
  } = deps;

  const shopSkins = model.shopSkins;
  const ownedIds = getCachedOwnedIds();
  const equippedId = getCachedEquipped();
  const centerX = CANVAS_WIDTH / 2;
  const cw = CANVAS_WIDTH;
  const idx = Math.min(model.shopViewIndex, Math.max(0, shopSkins.length - 1));
  const char = shopSkins[idx] ?? null;
  const g = menuGap();

  const uiRects: UiRects = { shopBack: undefined };

  // ── Top section: title + restock (always render) ──
  const titleY = UI_CLOSE_Y + uiCloseSize() / 2;
  ctx.fillStyle = MENU_TEXT_PRIMARY;
  ctx.font = `bold 10px ${GAME_FONT}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("SHOP", centerX, titleY);

  // Restock timer
  const now = new Date();
  const nextUtcMidnightMs = Date.UTC(
    now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1, 0, 0, 0, 0
  );
  const secToRefresh = Math.max(0, Math.floor((nextUtcMidnightMs - Date.now()) / 1000));
  ctx.fillStyle = "#7ca8ea";
  ctx.font = mFontPlain(5);
  ctx.fillText(`RESTOCK ${formatHms(secToRefresh)}`, centerX, titleY + 16);

  // Circle close button (top-right, always render)
  const backRect: Rect = { x: CANVAS_WIDTH - uiCloseSize() - UI_EDGE_PAD, y: UI_CLOSE_Y, w: uiCloseSize(), h: uiCloseSize() };
  drawCircleCloseButton(backRect);
  uiRects.shopBack = backRect;

  // Gem shop door button (top left, always render) — preserve aspect ratio
  const doorH = isMobileViewport() ? 130 : 108;
  const doorAspect = doorImg ? (doorImg.naturalWidth / doorImg.naturalHeight) || 1 : 0.7;
  const doorW = Math.round(doorH * doorAspect);
  const doorX = UI_EDGE_PAD;
  const doorY = 0;
  const doorRect: Rect = { x: doorX, y: doorY, w: doorW, h: doorH };
  const doorCX = doorX + doorW / 2;
  const doorCY = doorY + doorH / 2;

  const glowPulse = 0.3 + 0.2 * Math.sin(nowMs() / 600);
  if (doorImg) {
    ctx.save();
    ctx.globalAlpha = glowPulse;
    ctx.filter = "blur(6px) brightness(3)";
    ctx.drawImage(doorImg, doorX, doorY, doorW, doorH);
    ctx.filter = "none";
    ctx.restore();
  }
  if (doorImg) {
    ctx.drawImage(doorImg, doorX, doorY, doorW, doorH);
  } else {
    drawBtn(doorRect, "\u{1F48E}", { fill: "#1a3340", stroke: "#64d8ff", font: mFont("bold", 10) });
  }
  ctx.save();
  ctx.fillStyle = "#ffffff";
  ctx.font = mFont("bold", 6);
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.shadowColor = "rgba(0,0,0,0.9)";
  ctx.shadowBlur = 4;
  ctx.fillText("BUY", doorCX, doorCY - 4);
  ctx.fillText("GEMS", doorCX, doorCY + 6);
  ctx.restore();
  uiRects.gemShop = doorRect;

  // ── Empty / loading state ──
  if (char == null) {
    ctx.fillStyle = "rgba(255,255,255,0.5)";
    ctx.font = mFont("bold", 7);
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("LOADING...", centerX, canvasH / 2);
    return uiRects;
  }

  // Detect index change for slide animation
  if (slideState.shopPrevIdx !== idx && slideState.shopPrevIdx >= 0) {
    const prevSkin = shopSkins[slideState.shopPrevIdx];
    if (prevSkin) slideState.shopPrevSkinPath = prevSkin.imageSmallPath;
    const diff = idx - slideState.shopPrevIdx;
    // Handle wrap-around direction
    if (diff === 1 || diff === -(shopSkins.length - 1)) {
      slideState.shopSlideDir = 1;
    } else {
      slideState.shopSlideDir = -1;
    }
    slideState.shopSlideStartMs = performance.now();
  }
  slideState.shopPrevIdx = idx;

  // Slide animation progress
  const slideDur = 300;
  const slideElapsed = performance.now() - slideState.shopSlideStartMs;
  const slideT = Math.min(1, slideElapsed / slideDur);
  const slideEased = 1 - (1 - slideT) * (1 - slideT); // easeOutQuad
  const isSliding = slideT < 1;

  const owned = ownedIds.includes(char.id);
  const equipped = equippedId === char.id;
  const rarityColor = char.rarity === "EPIC"
    ? "#ff9500"
    : char.rarity === "RARE"
      ? "#a855f7"
      : "#64d8ff";

  // Dot indicators (keep at original vertical position)
  let curY = 90 + mobileFontSize(11) + g + mobileFontSize(5) + g;
  const shopDotSize = 6;
  const shopDotGap = 10;
  const shopDotsW = shopSkins.length * shopDotSize + (shopSkins.length - 1) * shopDotGap;
  const shopDotsX = centerX - shopDotsW / 2;
  for (let i = 0; i < shopSkins.length; i++) {
    const dx = shopDotsX + i * (shopDotSize + shopDotGap);
    ctx.beginPath();
    ctx.arc(dx + shopDotSize / 2, curY + shopDotSize / 2, shopDotSize / 2, 0, Math.PI * 2);
    ctx.fillStyle = i === idx ? "#ffffff" : "rgba(255,255,255,0.3)";
    ctx.fill();
  }
  curY += shopDotSize + g + 2;

  // Compute portrait position first so button can sit just above it
  const bobY = Math.sin(nowMs() / 800) * 2;
  const portraitH = canvasH * 0.667;
  const portraitW = portraitH * 0.85;
  const portraitTopRaw = canvasH - MENU_CONTENT_OFFSET_PX - portraitH;

  // Buy / Owned button (ready-button sized, centered, just above portrait)
  const menuCols = menuButtonColumns(centerX);
  const shopBtnW = menuCols.halfW;
  const shopBtnH = menuBtnH();
  const buyBtnY = portraitTopRaw + 10;
  const buyRect: Rect = { x: centerX - shopBtnW / 2, y: buyBtnY, w: shopBtnW, h: shopBtnH };
  if (!owned) {
    const canAfford = model.gems >= char.priceGems;
    // Rarity-tinted background
    const buyFill = canAfford
      ? (char.rarity === "EPIC" ? "rgba(60,30,0,0.9)"
        : char.rarity === "RARE" ? "rgba(35,15,60,0.9)"
        : "rgba(15,30,50,0.9)")
      : "rgba(15,15,25,0.8)";
    // Rarity color as stroke — makes rarity obvious
    const buyStroke = canAfford ? rarityColor : "#555";
    drawBtn(buyRect, "", {
      fill: buyFill,
      stroke: buyStroke,
      variant: "lobby",
    });

    // Gem icon + price (no "BUY" text) — bigger
    const priceText = String(char.priceGems);
    ctx.font = mFont("bold", 7);
    const priceW = measureTextWidth(priceText);
    const iconSize = 16;
    const gap = 5;
    const totalW = iconSize + gap + priceW;
    const startX = buyRect.x + (buyRect.w - totalW) / 2;
    const textY = buyRect.y + shopBtnH / 2 + 1;

    drawGemIcon(startX, buyRect.y + (shopBtnH - iconSize) / 2, iconSize);
    ctx.fillStyle = canAfford ? "#e9f7ff" : "#666";
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillText(priceText, startX + iconSize + gap, textY);
    ctx.textAlign = "center";
    const buyKey = `shopBuy${idx}` as keyof UiRects;
    (uiRects as Record<string, Rect>)[buyKey] = buyRect;
  } else {
    // Owned: ready-state green style
    const ownedLabel = equipped ? "EQUIPPED" : "OWNED";
    drawBtn(buyRect, ownedLabel, {
      fill: "rgba(20,60,40,0.85)",
      stroke: "rgba(80,220,120,0.6)",
      font: mFont("bold", 5),
      variant: "lobby",
    });
  }

  // ── Large character popping out from bottom ──
  const portraitY = portraitTopRaw + bobY;
  const portraitBaseX = centerX - portraitW / 2;

  // New skin slides in from the direction of navigation
  // dir=1 (next): new comes from right, old exits left
  // dir=-1 (prev): new comes from left, old exits right
  const slideRange = cw * 0.6;
  const newOffX = (1 - slideEased) * slideState.shopSlideDir * slideRange;
  const oldOffX = -slideEased * slideState.shopSlideDir * slideRange;

  // Draw old skin sliding out
  if (isSliding && slideState.shopPrevSkinPath) {
    ctx.save();
    ctx.globalAlpha = 1 - slideEased;
    drawCharacterPortrait(slideState.shopPrevSkinPath, portraitBaseX + oldOffX, portraitY, portraitW, portraitH, "bottom");
    ctx.restore();
  }

  const newPX = portraitBaseX + (isSliding ? newOffX : 0);

  // Draw new character sliding in
  ctx.save();
  ctx.globalAlpha = isSliding ? slideEased : 1;
  drawCharacterPortrait(char.imageSmallPath, newPX, portraitY, portraitW, portraitH, "bottom");
  ctx.restore();

  // ── Arrow buttons on sides of character ──
  const arrowSize = uiCloseSize();
  const arrowBaseY = canvasH - MENU_CONTENT_OFFSET_PX - portraitH;
  const arrowY = arrowBaseY + portraitH * 0.55 - arrowSize / 2;
  const prevRect: Rect = { x: UI_EDGE_PAD, y: arrowY, w: arrowSize, h: arrowSize };
  const nextRect: Rect = { x: cw - arrowSize - UI_EDGE_PAD, y: arrowY, w: arrowSize, h: arrowSize };

  drawCircleArrowButton(prevRect, -1);
  uiRects.shopPrev = prevRect;

  drawCircleArrowButton(nextRect, 1);
  uiRects.shopNext = nextRect;

  return uiRects;
}
